// Google Tag Manager dataLayer helpers.
//
// GTM (see components/GoogleTagManager.tsx) initialises `window.dataLayer`
// and loads container GTM-T6F49W3N in production. To register a Google Ads
// conversion we push a named custom event into the dataLayer; a matching
// "Custom Event" trigger inside GTM then fires the Google Ads Conversion
// (and GA4) tag for that event.
//
// Keep the event names below in sync with the triggers configured in the
// GTM container:
//   • `book_visit_submit` — a "Book a visit" flow was completed.
//   • `text_us_submit`    — a "Text us" SMS conversation was started.
//
// These pushes are always safe to call: if GTM hasn't loaded (dev/staging,
// ad-blockers) the events simply queue on the plain array and are dropped.

type DataLayerEvent = Record<string, unknown> & { event: string }

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[]
  }
}

// GTM container-side triggers listen for these exact event names.
export const CONVERSION_EVENTS = {
  bookVisit: 'book_visit_submit',
  textUs: 'text_us_submit',
} as const

// Low-level push. Ensures `window.dataLayer` exists before pushing so an
// early call (before GTM's snippet runs) still enqueues rather than throws.
export function pushDataLayer(payload: DataLayerEvent): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

export interface BookVisitConversion {
  /** Which surface the booking came from, e.g. 'booking_modal' | 'booking_page'. */
  source: string
  service?: string
  petSpecies?: string
  preferredWindow?: string
}

// Fires when a visitor completes the "Book a visit" flow (lead submitted).
export function trackBookVisitConversion(details: BookVisitConversion): void {
  pushDataLayer({
    event: CONVERSION_EVENTS.bookVisit,
    conversion_type: 'book_visit',
    lead_source: details.source,
    service: details.service,
    pet_species: details.petSpecies,
    preferred_window: details.preferredWindow,
  })
}

export interface TextUsConversion {
  /** Which surface started the SMS conversation, e.g. 'website_text_us'. */
  source: string
}

// Fires when a visitor starts an SMS conversation via the "Text us" button.
export function trackTextUsConversion(details: TextUsConversion): void {
  pushDataLayer({
    event: CONVERSION_EVENTS.textUs,
    conversion_type: 'text_us',
    lead_source: details.source,
  })
}
